import React, { useEffect, useRef, useState } from 'react';
import { Lock, Printer, Trophy } from 'lucide-react';
import './Certificate.css';
import { certificateLabel } from '../utils/certificateLabel';
import { certificateBadge, certificateSubheading } from '../utils/certificateBadge';

const APP_URL = window.location.origin;

// Signs every certificate. The image is served from public/signatures; it is
// hidden if the file is missing, so the name still prints.
export const CERT_SIGNATORY = {
  name: 'Chengetai Myezwa',
  title: 'Managing Director',
  company: 'Okiru (Pty) Ltd',
  image: '/signatures/chengetai-myezwa.png',
};

// ── Per-course certificate templates ─────────────────────────────────────────
export const CERT_TEMPLATES = {
  'ai-microsoft-office': {
    accent:     '#0078D4',
    accent2:    '#06CDE1',
    headerGrad: 'linear-gradient(135deg, #003F7A 0%, #0078D4 55%, #06CDE1 100%)',
    borderTop:  '#0078D4',
    sealColor:  '#0078D4',
    // grid dots pattern
    pattern: `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='2' cy='2' r='1.5' fill='%230078D4' fill-opacity='0.12'/%3E%3C/svg%3E")`,
    ribbonColors: ['#0078D4', '#06CDE1', '#50E6FF'],
  },
  'safe-ai-compliance': {
    accent:     '#059669',
    accent2:    '#10E8A0',
    headerGrad: 'linear-gradient(135deg, #064E3B 0%, #059669 55%, #10E8A0 100%)',
    borderTop:  '#059669',
    sealColor:  '#059669',
    // hexagon pattern
    pattern: `url("data:image/svg+xml,%3Csvg width='40' height='46' viewBox='0 0 40 46' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 1 L38 11 L38 35 L20 45 L2 35 L2 11 Z' fill='none' stroke='%23059669' stroke-opacity='0.09' stroke-width='1.5'/%3E%3C/svg%3E")`,
    ribbonColors: ['#059669', '#10E8A0', '#06CDE1'],
  },
  'ai-reporting-analysis': {
    accent:     '#D84E00',
    accent2:    '#FFB300',
    headerGrad: 'linear-gradient(135deg, #7C2D00 0%, #D84E00 50%, #FFB300 100%)',
    borderTop:  '#D84E00',
    sealColor:  '#D84E00',
    // rising bars pattern
    pattern: `url("data:image/svg+xml,%3Csvg width='36' height='36' viewBox='0 0 36 36' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='2' y='20' width='6' height='14' fill='%23FF7512' fill-opacity='0.09' rx='1'/%3E%3Crect x='13' y='12' width='6' height='22' fill='%23FFB300' fill-opacity='0.09' rx='1'/%3E%3Crect x='24' y='4' width='6' height='30' fill='%23FF7512' fill-opacity='0.09' rx='1'/%3E%3C/svg%3E")`,
    ribbonColors: ['#D84E00', '#FF7512', '#FFB300'],
  },
  'prompt-mastery': {
    accent:     '#9747FF',
    accent2:    '#E91E63',
    headerGrad: 'linear-gradient(135deg, #3B0764 0%, #9747FF 55%, #E91E63 100%)',
    borderTop:  '#9747FF',
    sealColor:  '#9747FF',
    // wave curves pattern
    pattern: `url("data:image/svg+xml,%3Csvg width='60' height='30' viewBox='0 0 60 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 15 Q15 0 30 15 Q45 30 60 15' fill='none' stroke='%239747FF' stroke-opacity='0.1' stroke-width='2'/%3E%3C/svg%3E")`,
    ribbonColors: ['#9747FF', '#E91E63', '#BA0DA7'],
  },
  'mastering-claude-ai': {
    accent:     '#B8860B',
    accent2:    '#9747FF',
    headerGrad: 'linear-gradient(135deg, #4A3000 0%, #B8860B 45%, #FFD700 75%, #9747FF 100%)',
    borderTop:  '#B8860B',
    sealColor:  '#B8860B',
    // radial burst / star lines
    pattern: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='30' y1='30' x2='30' y2='0' stroke='%23FFD700' stroke-opacity='0.08' stroke-width='1.5'/%3E%3Cline x1='30' y1='30' x2='60' y2='30' stroke='%23FFD700' stroke-opacity='0.08' stroke-width='1.5'/%3E%3Cline x1='30' y1='30' x2='51' y2='9' stroke='%23FFD700' stroke-opacity='0.08' stroke-width='1.5'/%3E%3Cline x1='30' y1='30' x2='9' y2='9' stroke='%23FFD700' stroke-opacity='0.08' stroke-width='1.5'/%3E%3C/svg%3E")`,
    ribbonColors: ['#B8860B', '#FFD700', '#9747FF'],
  },
  'sharepoint-business': {
    accent:     '#0078D4',
    accent2:    '#2B88D8',
    headerGrad: 'linear-gradient(135deg, #003566 0%, #0078D4 55%, #2B88D8 100%)',
    borderTop:  '#0078D4',
    sealColor:  '#0078D4',
    pattern: `url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='2' y='2' width='12' height='12' fill='none' stroke='%230078D4' stroke-opacity='0.1' stroke-width='1.5' rx='1'/%3E%3Crect x='18' y='2' width='12' height='12' fill='none' stroke='%230078D4' stroke-opacity='0.1' stroke-width='1.5' rx='1'/%3E%3Crect x='2' y='18' width='12' height='12' fill='none' stroke='%230078D4' stroke-opacity='0.1' stroke-width='1.5' rx='1'/%3E%3Crect x='18' y='18' width='12' height='12' fill='none' stroke='%230078D4' stroke-opacity='0.1' stroke-width='1.5' rx='1'/%3E%3C/svg%3E")`,
    ribbonColors: ['#0078D4', '#2B88D8', '#50E6FF'],
  },
  'dynamics-365-crm': {
    accent:     '#742774',
    accent2:    '#A855A8',
    headerGrad: 'linear-gradient(135deg, #3B0040 0%, #742774 55%, #A855A8 100%)',
    borderTop:  '#742774',
    sealColor:  '#742774',
    pattern: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='20' cy='20' r='14' fill='none' stroke='%23742774' stroke-opacity='0.08' stroke-width='2'/%3E%3Ccircle cx='20' cy='20' r='7' fill='none' stroke='%23A855A8' stroke-opacity='0.08' stroke-width='2'/%3E%3C/svg%3E")`,
    ribbonColors: ['#742774', '#A855A8', '#E879E8'],
  },
};

const DEFAULT_TEMPLATE = {
  accent:     '#38BDF8',
  accent2:    '#0EA5E9',
  headerGrad: 'linear-gradient(135deg, #0F172A 0%, #38BDF8 60%, #0EA5E9 100%)',
  borderTop:  '#38BDF8',
  sealColor:  '#38BDF8',
  pattern:    'none',
  ribbonColors: ['#38BDF8', '#0EA5E9', '#1E293B'],
};

// Colours from CERT_TEMPLATES; the words (subheading, label) from
// src/data/certificateBadges.json, shared with the public verify page.
export function getTemplate(courseId) {
  return { ...(CERT_TEMPLATES[courseId] || DEFAULT_TEMPLATE), subheading: certificateSubheading(courseId), label: certificateBadge(courseId) };
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Ribbon bar (3 colored strips across the bottom) ──────────────────────────
function RibbonBar({ colors }) {
  return (
    <div className="cert-ribbon" aria-hidden="true">
      {colors.map((c, i) => <div key={i} className="cert-ribbon-strip" style={{ background: c }} />)}
    </div>
  );
}

// ── Seal (decorative circle in bottom-left) ───────────────────────────────────
function CertSeal({ color, score, delivery }) {
  const live = delivery === 'live';
  return (
    <div className="cert-seal" style={{ borderColor: color, boxShadow: `0 0 0 3px ${color}22` }}>
      <div className="cert-seal-inner" style={{ background: color + '15' }}>
        {live
          ? <div className="cert-seal-score" style={{ color, fontSize: 14 }}>LIVE</div>
          : <div className="cert-seal-score" style={{ color }}>{score ?? ' | '}<span>%</span></div>}
        <div className="cert-seal-label">{live ? 'SESSION' : 'SCORE'}</div>
      </div>
    </div>
  );
}

// ── Header band ───────────────────────────────────────────────────────────────
function CertHeader({ tpl, subheading }) {
  return (
    <div className="cert-header-band" style={{ background: tpl.headerGrad }}>
      <div className="cert-header-left">
        <img src="/okiru-logo-white.png" alt="Okiru" height={32}
          style={{ display: 'block', objectFit: 'contain', mixBlendMode: 'screen' }} />
      </div>
      <div className="cert-header-right">
        <div className="cert-header-cert">CERTIFICATE</div>
        <div className="cert-header-sub">{subheading}</div>
      </div>
    </div>
  );
}

// ── Single certificate document ───────────────────────────────────────────────
// The QR code comes from the server's /api/cert-qr endpoint, which encodes the
// verify link from APP_URL, so it points at the right address in each
// environment and nothing is sent to a third-party QR service. qrSrc can still
// override it.
export function CertDocument({ course, user, certData, certRef, qrSrc }) {
  const tpl       = getTemplate(course.id);
  const issued    = certData?.issuedAt ? fmtDate(certData.issuedAt) : fmtDate(new Date());
  const certId    = certData?.certId;
  const verifyUrl = certId ? `${APP_URL}/verify/${certId}` : null;

  return (
    <div className="cert-doc" ref={certRef}
      style={{ borderTop: `5px solid ${tpl.borderTop}`, backgroundImage: tpl.pattern }}>

      <CertHeader tpl={tpl} subheading={tpl.subheading} />

      <div className="cert-doc-body">
        <p className="cert-presented">This certificate is proudly presented to</p>
        <h2 className="cert-doc-name">{user.name}</h2>
        <div className="cert-divider" style={{ background: `linear-gradient(90deg, transparent, ${tpl.accent}, ${tpl.accent2}, transparent)` }} />
        <p className="cert-completion-text">
          in recognition of successfully completing
        </p>
        <p className="cert-course-title">{course.title}</p>
        <p className="cert-category-badge" style={{ color: tpl.accent, borderColor: tpl.accent + '40', background: tpl.accent + '0D' }}>
          {certificateBadge(course.id, course.category)}
        </p>
      </div>

      <div className="cert-signature">
        <img
          src={CERT_SIGNATORY.image}
          alt={`Signature of ${CERT_SIGNATORY.name}`}
          className="cert-signature-img"
          height={44}
          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
        />
        <div className="cert-signature-line" />
        <div className="cert-signature-name">{CERT_SIGNATORY.name}</div>
        <div className="cert-signature-co">{CERT_SIGNATORY.title}, {CERT_SIGNATORY.company}</div>
      </div>

      <div className="cert-doc-footer">
        <CertSeal color={tpl.sealColor} score={certData?.score} delivery={certData?.delivery} />

        <div className="cert-footer-mid">
          <div className="cert-issue-date">
            <span className="cert-footer-label">Date of Issue</span>
            <span className="cert-footer-val">{issued}</span>
          </div>
          {verifyUrl && (
            <div className="cert-doc-qr-block">
              <a href={verifyUrl} target="_blank" rel="noopener noreferrer" className="cert-doc-qr-link">
                <img
                  src={qrSrc || `/api/cert-qr/${certId}`}
                  alt="Scan to verify"
                  className="cert-doc-qr"
                  width={72} height={72} crossOrigin="anonymous"
                />
              </a>
              <span className="cert-doc-qr-label">Scan to verify</span>
            </div>
          )}
          <div className="cert-cert-id">
            <span className="cert-footer-label">Certificate number</span>
            <span className="cert-footer-val cert-footer-val--mono">{certificateLabel(certData?.certNumber, certId) || 'Not issued'}</span>
          </div>
        </div>
      </div>

      <p className="cert-disclaimer">
        This certificate is issued by Okiru's AI Training Division and confirms completion of the stated course. It does not constitute a formal academic qualification.
      </p>
      <RibbonBar colors={tpl.ribbonColors} />
    </div>
  );
}

// ── Locked cert preview ───────────────────────────────────────────────────────
function LockedCert({ course }) {
  const tpl = getTemplate(course.id);
  return (
    <div className="cert-locked-wrap">
      <div className="cert-doc cert-doc--locked" aria-label="Locked certificate"
        style={{ borderTop: `5px solid ${tpl.borderTop}`, backgroundImage: tpl.pattern }}>
        <CertHeader tpl={tpl} subheading={tpl.subheading} />
        <div className="cert-doc-body cert-doc-inner--blurred">
          <p className="cert-presented">This certificate is proudly presented to</p>
          <h2 className="cert-doc-name">Your Name Here</h2>
          <div className="cert-divider" style={{ background: `linear-gradient(90deg, transparent, ${tpl.accent}, ${tpl.accent2}, transparent)` }} />
          <p className="cert-completion-text">in recognition of successfully completing</p>
          <p className="cert-course-title">{course.title}</p>
        </div>
        <div className="cert-lock-overlay">
          <div className="cert-lock-icon"><Lock size={28} strokeWidth={1.75} /></div>
          <div className="cert-lock-title">LOCKED</div>
          <div className="cert-lock-msg">
            Complete <strong>{course.title}</strong> and score at least <strong>{course.passMark ?? 75}%</strong> in the final assessment to unlock.
          </div>
        </div>
        <RibbonBar colors={tpl.ribbonColors} />
      </div>
      <div className="cert-locked-label">{course.title}</div>
    </div>
  );
}

function downloadCertPDF() { window.print(); }

// ── Earned cert card ──────────────────────────────────────────────────────────
function EarnedCert({ course, user, certData }) {
  const certRef   = useRef(null);
  const certId    = certData?.certId;
  const verifyUrl = certId ? `${APP_URL}/verify/${certId}` : null;

  return (
    <div className="cert-earn-wrap">
      <CertDocument course={course} user={user} certData={certData} certRef={certRef} />
      <div className="cert-earn-actions">
        <button className="btn btn-primary" onClick={downloadCertPDF}>
          <Printer size={16} strokeWidth={1.75} /> Print / Save as PDF
        </button>
        {verifyUrl && (
          <a href={verifyUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
            View Verification Page →
          </a>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Certificate({ user, enrollments, progress, certificates, navigateTo }) {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    fetch('/api/courses').then(r => r.json()).then(j => setCourses(j.data || [])).catch(() => {});
  }, []);

  const allEnrolled = courses.filter(c => enrollments.has(c.id));
  const earned      = allEnrolled.filter(c => certificates?.[c.id]);
  const locked      = courses.filter(c => !certificates?.[c.id]);

  if (!courses.length) return null;

  return (
    <div className="certs-page">
      <div className="certs-header">
        <h2>Your Certificates</h2>
        <p className="certs-sub">{earned.length} earned · {locked.length} locked</p>
      </div>

      {earned.length > 0 && (
        <section>
          <p className="certs-section-label">Earned</p>
          <div className="certs-earned-list">
            {earned.map(c => (
              <EarnedCert key={c.id} course={c} user={user} certData={certificates[c.id]} />
            ))}
          </div>
        </section>
      )}

      {locked.length > 0 && (
        <section>
          <p className="certs-section-label">Locked. Complete each course and pass its final assessment to unlock.</p>
          <div className="certs-locked-grid">
            {locked.map(c => <LockedCert key={c.id} course={c} />)}
          </div>
        </section>
      )}

      {earned.length === 0 && (
        <div className="cert-empty">
          <p className="cert-empty-icon"><Trophy size={40} strokeWidth={1.75} /></p>
          <h3>No certificates yet</h3>
          <p>Complete all lessons in a course and pass the final assessment to earn your certificate. The pass mark is shown on each course.</p>
          <button className="btn btn-primary" onClick={() => navigateTo('catalogue')}>Browse Courses</button>
        </div>
      )}
    </div>
  );
}
